import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { fetchFieldImageDetailStart } from "../../../store/features/digitalTwin/fieldImagesSlice";

const getImageSrc = (image) => {
  const value =
    image?.image_base64 ||
    image?.image ||
    image?.image_data ||
    image?.image_base_64 ||
    image?.file_base64 ||
    image?.base64 ||
    image?.content;

  if (!value) return null;
  if (value.startsWith("data:")) return value;
  return `data:image/jpeg;base64,${value}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

export default function FieldImagesTooltip({
  images: rawImages,
  imagesData,
  coordinates,
  selectedImage,
  selectedImageId,
  isLoading,
  isDetailLoading,
}) {
  const dispatch = useDispatch();
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const images = useMemo(() => {
    if (Array.isArray(rawImages)) return rawImages;
    if (Array.isArray(imagesData?.data)) return imagesData.data;
    if (Array.isArray(imagesData)) return imagesData;
    return [];
  }, [rawImages, imagesData]);

  const visibleImages = useMemo(() => images.slice(0, 4), [images]);

  const hasSelectedImageInList = visibleImages.some(
    (image) => String(image.id) === String(selectedImageId),
  );

  const activeImageId = hasSelectedImageInList
    ? selectedImageId
    : visibleImages[0]?.id;

  const activeImage =
    String(selectedImage?.id) === String(activeImageId)
      ? selectedImage
      : visibleImages.find((image) => String(image.id) === String(activeImageId));

  const previewSrc = getImageSrc(activeImage);

  useEffect(() => {
    const firstImageId = visibleImages[0]?.id;
    if (!firstImageId || hasSelectedImageInList) return;

    dispatch(fetchFieldImageDetailStart({ image_id: firstImageId }));
  }, [dispatch, hasSelectedImageInList, visibleImages]);

  const formattedCoordinates = useMemo(() => {
    if (!coordinates) return null;
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      const lng = Number(coordinates[0]);
      const lat = Number(coordinates[1]);
      if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
        return `${lng.toFixed(5)}, ${lat.toFixed(5)}`;
      }
      return `${coordinates[0]}, ${coordinates[1]}`;
    }
    return String(coordinates);
  }, [coordinates]);

  if (!isLoading && !images.length) return null;

  return (
    <Box
      sx={{
        width: "250px",
        fontFamily: "Arial, sans-serif",
        color: "text.primary",
        p: 0.5,
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box sx={{ pr: 3, mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            fontSize: "14px",
            lineHeight: 1.2,
            color: "#1e293b",
          }}
        >
          Storico immagini
        </Typography>
        {formattedCoordinates && (
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontSize: "11px",
              display: "block",
              mt: 0.25,
            }}
          >
            {formattedCoordinates}
          </Typography>
        )}
      </Box>

      {/* Image Preview Box */}
      <Box
        onClick={() => previewSrc && setFullscreenOpen(true)}
        title={previewSrc ? "Clicca per ingrandire" : undefined}
        sx={{
          width: "100%",
          height: 140,
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: "#f1f5f9",
          border: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: previewSrc ? "pointer" : "default",
          position: "relative",
          transition: "box-shadow 0.2s ease",
          "&:hover": previewSrc
            ? {
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                "& .zoom-icon": {
                  opacity: 1,
                },
              }
            : {},
        }}
      >
        {previewSrc && !isDetailLoading ? (
          <>
            <Box
              component="img"
              src={previewSrc}
              alt="Storico immagine"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box
              className="zoom-icon"
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                borderRadius: "50%",
                p: 0.3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.2s ease",
              }}
            >
              <ZoomInIcon sx={{ fontSize: 16 }} />
            </Box>
          </>
        ) : (
          <Box sx={{ color: "#94a3b8", textAlign: "center", p: 1 }}>
            {isLoading || isDetailLoading ? (
              <CircularProgress size={22} sx={{ mb: 0.5, color: "#64748b" }} />
            ) : (
              <ImageOutlinedIcon fontSize="small" />
            )}
            <Typography variant="caption" display="block" sx={{ fontSize: "11px" }}>
              {isLoading || isDetailLoading ? "Caricamento..." : "Nessuna anteprima"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Metadata: Classification & Timestamp */}
      {activeImage && (
        <Box
          sx={{
            mt: 0.75,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              color: "#334155",
              maxWidth: "150px",
            }}
            noWrap
          >
            {activeImage.classification || "-"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#64748b", fontSize: "11px", fontWeight: 500 }}
          >
            {formatDate(activeImage.timestamp)}
          </Typography>
        </Box>
      )}

      {/* Date Switcher Buttons (if multiple images) */}
      {visibleImages.length > 1 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(visibleImages.length, 4)}, 1fr)`,
            gap: 0.75,
            mt: 1,
          }}
        >
          {visibleImages.map((image) => {
            const isSelected = String(image.id) === String(activeImageId);
            return (
              <ButtonBase
                key={image.id}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    fetchFieldImageDetailStart({ image_id: image.id }),
                  );
                }}
                sx={{
                  py: 0.6,
                  px: 0.3,
                  borderRadius: "6px",
                  border: "1.5px solid",
                  borderColor: isSelected ? "primary.main" : "#cbd5e1",
                  bgcolor: isSelected ? "primary.main" : "#f8fafc",
                  color: isSelected ? "#fff" : "#475569",
                  fontSize: "10px",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  textAlign: "center",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: isSelected ? "primary.dark" : "#f1f5f9",
                  },
                }}
              >
                {formatDate(image.timestamp)}
              </ButtonBase>
            );
          })}
        </Box>
      )}

      {/* Fullscreen Preview Modal */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth="md"
      >
        <DialogContent
          sx={{
            p: 1,
            bgcolor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {previewSrc && (
            <Box
              component="img"
              src={previewSrc}
              alt="Immagine ingrandita"
              sx={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
                borderRadius: "4px",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
